import { useSuspenseQuery } from '@apollo/client'
import { FetchViewGroupsDocument, type ViewGroupFieldsFragment } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-common'
import { Array, HashMap, Option, pipe } from 'effect'

// apollo側のViewGroupのドメイン側の命名
export interface ViewItem {
  id: string
  viewGroupId?: string | null
  private: boolean
}
export type ViewDirectory = Pick<
  ViewGroupFieldsFragment,
  'id' | 'viewGroupId' | 'private' | 'type' | 'archived' | 'createdBy' | 'name'
>

export const useViewDirectories = (): ViewDirectory[] => {
  const organizationId = getOrganizationIdFromPath()

  const {
    data: { viewGroups },
  } = useSuspenseQuery(FetchViewGroupsDocument, {
    variables: {
      organizationId,
    },
    fetchPolicy: 'cache-and-network',
  })

  return viewGroups
}

const searchViewDirectoryAncestors = (
  id: string,
  mapper: HashMap.HashMap<string, ViewDirectory>,
  depth = 0,
): ViewDirectory[] => {
  if (depth > 20) {
    // ありえないはずだが、循環参照があった場合に無限ループになるので、念のため
    return []
  }

  return pipe(
    HashMap.get(mapper, id),
    Option.map((parent) => [
      parent,
      // eslint-disable-next-line eqeqeq
      ...(parent.viewGroupId == null ? [] : searchViewDirectoryAncestors(parent.viewGroupId, mapper, depth + 1)),
    ]),
    Option.getOrElse(() => []),
  )
}

/**
 * 自分と先祖を配列で返す
 */
export const getViewDirectoryAncestors = (
  viewGroupId: string | undefined | null,
  viewDirectories: ViewDirectory[],
): ViewDirectory[] => {
  // eslint-disable-next-line eqeqeq
  if (viewGroupId == null) return []

  const viewGroupsMap = pipe(
    viewDirectories,
    Array.map((x): [string, ViewDirectory] => [x.id, x]),
    HashMap.fromIterable,
  )

  return searchViewDirectoryAncestors(viewGroupId, viewGroupsMap)
}

export const shouldPrivate = (view: ViewItem, viewDirectories: ViewDirectory[]): boolean =>
  Array.head(getViewDirectoryAncestors(view.viewGroupId, viewDirectories)).pipe(
    Option.map((it) => it.private),
    Option.getOrElse(() => view.private),
  )
